import React, { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { PenLine, Sparkles } from "lucide-react";
import { ClassIcon } from "../ClassIcon";

const COLORS = ["#FF007F", "#00F0FF", "#FFE600", "#39FF14", "#9D00FF"];

export function SigningSplash({ w, onClose }) {
  const cls = w.class_info || {};
  const color = cls.color || "#00F0FF";
  const confetti = useMemo(() => Array.from({ length: 50 }, (_, i) => ({
    left: Math.random() * 100, delay: Math.random() * 0.5, dur: 1.8 + Math.random() * 1.6,
    color: COLORS[i % COLORS.length], w: 6 + Math.random() * 6, h: 8 + Math.random() * 10,
    drift: (Math.random() - 0.5) * 220, rot: Math.random() * 360,
  })), []);
  useEffect(() => {
    const t = setTimeout(onClose, 3200);
    const k = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => { clearTimeout(t); window.removeEventListener("keydown", k); };
  }, [onClose]);

  return createPortal(
    <div data-testid="signing-splash" onClick={onClose}
         className="ach-pop-fade fixed inset-0 z-[9998] flex items-center justify-center bg-[#0A051A]/80 backdrop-blur-md cursor-pointer overflow-hidden">
      <div className="ach-pop-rays absolute left-1/2 top-1/2 w-[160vmax] h-[160vmax] -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
      {confetti.map((c, i) => (
        <span key={i} className="ach-pop-confetti absolute top-0 pointer-events-none"
              style={{ left: `${c.left}%`, width: c.w, height: c.h, background: c.color, boxShadow: `0 0 6px ${c.color}`,
                       animationDelay: `${c.delay}s`, animationDuration: `${c.dur}s`, "--drift": `${c.drift}px`, "--rot": `${c.rot}deg` }} />
      ))}
      <div className="ach-pop-card relative text-center px-6">
        <div className="signing-stamp inline-flex items-center gap-2 px-4 py-1.5 mb-4 rounded-full border-2 font-heading text-xs uppercase tracking-[0.35em]"
             style={{ color: "#FFE600", borderColor: "#FFE600", background: "#0A051ACC", boxShadow: "0 0 18px #FFE60088" }}>
          <PenLine size={14} /> Contract Signed
        </div>
        <div className="font-graffiti text-2xl sm:text-3xl text-[#C9BDF0] mb-1" style={{ textShadow: "2px 2px 0 #000" }}>New Signing!</div>
        <h2 data-testid="signing-splash-name" className="signing-name font-graffiti text-5xl sm:text-7xl leading-none break-words"
            style={{ color: "#fff", textShadow: `0 0 24px ${color}, 0 0 60px #FF007F88, 4px 4px 0 #000` }}>
          {w.name}
        </h2>
        {cls.id && (
          <div data-testid="signing-splash-class" className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 bg-[#0A051A]/85 font-heading text-sm uppercase tracking-widest"
               style={{ color, borderColor: color, boxShadow: `0 0 14px ${color}77` }}>
            <ClassIcon classId={cls.id} color={color} size={18} /> {cls.name}
          </div>
        )}
        <div className="mt-5 text-[11px] font-mono text-[#A89BCC] flex items-center justify-center gap-1.5">
          <Sparkles size={12} className="text-[#FFE600]" /> joins the roster · tap to continue
        </div>
      </div>
    </div>,
    document.body
  );
}
